import {Injectable} from '@angular/core';
import {differenceInDays, distanceInWordsStrict, format, getTime} from 'date-fns';
import {environment} from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DateService {
  public static readonly LENGTH_OF_DAY: number = 1000 * 60 * 60 * 24;
  public static readonly DAYS_ALLOWED_IN_SES: number = 42;

  public formatDate(date: Date | string | number, formatOverride?: string, locale?: Object): string {
    return formatOverride
      ? format(date, formatOverride, {locale})
      : format(date, environment.datetime.defaultDateFormat, {locale});
  }

  public formatTime(date: Date | string | number, formatOverride?: string, locale?: Object): string {
    return formatOverride
      ? format(date, formatOverride, {locale})
      : format(date, environment.datetime.defaultTimeFormat, {locale});
  }

  /**
   * Return the duration as an amount of time passed, e.g. '1 hour', '10 days'
   */
  public formatDuration(duration: Date | string | number, locale?: Object): string {
    return distanceInWordsStrict(0, duration, {locale});
  }

  public formatDurationDays(duration: Date | string | number): string {
    return distanceInWordsStrict(0, duration, {unit: 'd', partialMethod: 'floor'});
  }

  /**
   * Converts a date representation to a timestamp.
   *
   * @param date {Date | string | number}    The date as a Date or string or number
   * @returns    {number}                    A timestamp in milliseconds
   */
  public getTime(date: Date | string | number): number {
    return getTime(date);
  }

  public getTimeInDays(time: number): number {
    return time || time === 0 ? Math.ceil(time / DateService.LENGTH_OF_DAY) : null;
  }

  /**
   * Subtracts the given time from the given number of days and returns the result in days.
   *
   * @param days            {number}     The number of days to subtract from
   * @param timeToSubtract  {number}     The time to subtract in milliseconds
   * @returns               {number}     The resultant number of days
   */
  public subtractTimeFromDays(days: number, timeToSubtract: number): number {
    const daysInMillis: number = days * DateService.LENGTH_OF_DAY;
    if (timeToSubtract > daysInMillis) {
      return differenceInDays(daysInMillis, timeToSubtract);
    }
    return differenceInDays(daysInMillis, timeToSubtract) + 1;
  }

  public getDuration(time: number): string {
    return time ? this.formatDurationDays(time) : null;
  }

  public getTimeLeftInSesAsString(timeRemainingInSes: number): string {
    const daysLeft: number = this.getTimeInDays(timeRemainingInSes);
    return this.translateDaysRemainingToString(daysLeft);
  }

  public isTimeAllowedInSES(value: number): boolean {
    return value === DateService.DAYS_ALLOWED_IN_SES * DateService.LENGTH_OF_DAY;
  }

  public translateDaysRemainingToString(daysLeft: number): string {
    const absoluteValue: number = Math.abs(daysLeft);
    if (daysLeft === 1) {
      return '1 day remaining';
    } else if (daysLeft > 0) {
      return `${absoluteValue} days remaining`;
    } else if (daysLeft === 0) {
      return 'Due';
    } else if (daysLeft === -1) {
      return `1 day since due`;
    } else if (daysLeft === null) {
      return '';
    } else {
      return `${absoluteValue} days since due`;
    }
  }
}
